import { useEffect, useMemo, useRef } from "react";
import "./PayoutFlash.css";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";

/**
 * PayoutFlash — floating-center cinematic that fires on a big win.
 *
 * Three visual variants, all share the same API so consumers can swap
 * them without touching call sites:
 *   - "skull"   → Chillet skull image burst + radial particle sparkles
 *   - "firefly" → reuses the existing Firefly clip (matches Work feature)
 *   - "sparkle" → SVG/CSS confetti burst (no images, no video, lightest)
 *   - "defeat"  → muted rose pulse with no sparkles, no skull. Used as
 *                 the loss-side cinematic so defeats feel acknowledged
 *                 without being celebratory.
 *
 * Props:
 *   show      bool   — render & start the animation when true
 *   amount    number — skull payout to display (optional)
 *   label     string — small caption ("BIG WIN", "DAILY BONUS", …)
 *   variant   "skull" | "firefly" | "sparkle"
 *   bonus     bool   — louder "rare event" treatment: golden screen
 *                      flash, larger amount, extended duration
 *   duration  ms     — total on-screen time (default 2000)
 *   onDone    fn     — fires after the animation completes
 */
export function PayoutFlash({
  show,
  amount = 0,
  label = "BIG WIN",
  variant = "skull",
  bonus = false,
  duration,
  onDone,
}) {
  // Rare bonus events get a longer window so the player can read the
  // label and savor it — 3.2s vs the normal 2s.
  const effectiveDuration = duration ?? (bonus ? 3200 : 2000);
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => onDone?.(), effectiveDuration);
    return () => clearTimeout(t);
  }, [show, effectiveDuration, onDone]);

  if (!show) return null;

  return (
    <div
      className={
        `payout-flash` +
        (variant === "defeat" ? " payout-flash--defeat" : "") +
        (bonus ? " payout-flash--bonus" : "")
      }
      role="presentation"
      onClick={() => onDone?.()}
      data-testid={`payout-flash-${variant}${bonus ? "-bonus" : ""}`}
    >
      <div className="payout-flash__card" onClick={(e) => e.stopPropagation()}>
        {variant === "skull" && <SkullBurst />}
        {variant === "firefly" && <FireflyClip duration={effectiveDuration} />}
        {variant === "sparkle" && <SparkleConfetti />}
        {variant === "defeat" && <DefeatPulse />}

        {label && <div className="payout-flash__label">{label}</div>}
        {amount > 0 && (
          <div
            className={`payout-flash__amount${variant === "defeat" ? " payout-flash__amount--loss" : ""}`}
            data-testid="payout-flash-amount"
          >
            {variant === "defeat" ? "-" : "+"}{amount.toLocaleString()}{" "}
            <ChilletCoinIcon size={bonus ? 32 : 24} className="payout-flash__skull" />
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────── Variant A — Chillet Skull burst ───────────────── */
function SkullBurst() {
  // 14 radial particles, pre-computed angles so the burst is symmetrical
  // but feels organic. Mix of teal + gold to match the brand glow.
  const particles = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const angle = (i / 14) * Math.PI * 2 + (Math.random() - 0.5) * 0.2;
        const dist = 110 + Math.random() * 70;
        return {
          dx: Math.cos(angle) * dist,
          dy: Math.sin(angle) * dist,
          delay: Math.random() * 0.15,
          color: i % 2 === 0 ? "var(--teal)" : "#FFD700",
          size: 6 + Math.random() * 6,
        };
      }),
    []
  );

  return (
    <div className="skullburst">
      <div className="skullburst__halo" />
      {particles.map((p, i) => (
        <span
          key={i}
          className="skullburst__particle"
          style={{
            "--dx": `${p.dx}px`,
            "--dy": `${p.dy}px`,
            "--c": p.color,
            "--size": `${p.size}px`,
            "--delay": `${p.delay}s`,
          }}
        />
      ))}
      <img
        src="/icons/chillet_coin_heads.png"
        alt=""
        className="skullburst__icon"
        draggable={false}
      />
    </div>
  );
}

/* ───────────────── Variant B — Firefly clip ───────────────── */
function FireflyClip({ duration }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    try { v.currentTime = 0; } catch (_) {}
    v.play().catch(() => {});
    // Hard stop so the clip never outlives the cinematic window
    const t = setTimeout(() => { try { v.pause(); } catch (_) {} }, duration);
    return () => clearTimeout(t);
  }, [duration]);

  return (
    <div className="fireflyclip">
      <video
        ref={ref}
        src="/assets/work/firefly_work.mp4"
        poster="/assets/work/firefly_work_poster.jpg"
        muted
        playsInline
        preload="auto"
        disablePictureInPicture
        className="fireflyclip__video"
      />
    </div>
  );
}

/* ───────────────── Variant D — Defeat pulse ───────────────── */
function DefeatPulse() {
  // A single rose ring that expands once, no particles, no skull. The
  // intent is "acknowledged loss" without celebration.
  return (
    <div className="defeatpulse" aria-hidden="true">
      <div className="defeatpulse__ring" />
      <div className="defeatpulse__icon">✖</div>
    </div>
  );
}

/* ───────────────── Variant C — SVG/CSS confetti sparkle ───────────────── */
function SparkleConfetti() {
  const shards = useMemo(
    () =>
      Array.from({ length: 36 }, (_, i) => {
        const angle = (i / 36) * Math.PI * 2 + (Math.random() - 0.5) * 0.35;
        const dist = 90 + Math.random() * 110;
        const palette = ["#00E5FF", "#FFD700", "#A06CFF", "#7FE3C4"];
        return {
          dx: Math.cos(angle) * dist,
          dy: Math.sin(angle) * dist,
          rot: Math.random() * 720 - 360,
          delay: Math.random() * 0.25,
          color: palette[i % palette.length],
          shape: i % 3, // 0=star, 1=bar, 2=diamond
        };
      }),
    []
  );

  return (
    <div className="sparkle">
      <div className="sparkle__ring" />
      {shards.map((s, i) => (
        <span
          key={i}
          className={`sparkle__shard sparkle__shard--${s.shape}`}
          style={{
            "--dx": `${s.dx}px`,
            "--dy": `${s.dy}px`,
            "--rot": `${s.rot}deg`,
            "--c": s.color,
            "--delay": `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
